import React, { useState, useEffect, useCallback, useRef } from 'react';
import { 
  Button, Table, message, Modal, Form, Select, Switch, InputNumber, 
  Input, Checkbox, Space, Card, Tag, Tooltip 
} from 'antd';
import { 
  PlusOutlined, SearchOutlined, CloseCircleOutlined, ReloadOutlined 
} from '@ant-design/icons';
import { 
  getFundRules, createFundRule, updateFundRule, deleteFundRule, getBankList,
  createBank, getBankNames, getBankCountrys, getProjectList, getFullKeyList,
  getFundStatus, toggleFundStatus
} from '../../services/api';

const { Option } = Select;

const FundRules = () => {
  const [rules, setRules] = useState([]);
  const [banks, setBanks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingRule, setEditingRule] = useState(null);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10, total: 0 });
  const [form] = Form.useForm();
  const [beneficiaryAdded, setBeneficiaryAdded] = useState(false); // State to track beneficiary_added checkbox
  const [fundSystemEnabled, setFundSystemEnabled] = useState(false);
  const [statusLoading, setStatusLoading] = useState(true);
  
  // Search and Filter States
  const [searchText, setSearchText] = useState('');
  const [selectedSource, setSelectedSource] = useState('all');
  const [selectedTarget, setSelectedTarget] = useState('all');
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [selectedBeneficiary, setSelectedBeneficiary] = useState('all');

  // Account creation state
  const [isAccountModalVisible, setIsAccountModalVisible] = useState(false);
  const [accountForm] = Form.useForm();
  const [projects, setProjects] = useState([]);
  const [bankNames, setBankNames] = useState([]);
  const [bankCountrys, setBankCountrys] = useState([]);
  const [keys, setKeys] = useState([]);
  const [answers, setAnswers] = useState([{ key: '', value: '' }]);

  const fetchRules = useCallback(async (page = 1, pageSize = 10, filterOverrides = {}) => {
    setLoading(true);
    try {
      const currentFilters = {
        search: filterOverrides.search !== undefined ? filterOverrides.search : searchText,
        source_account_id: filterOverrides.source_account_id !== undefined ? filterOverrides.source_account_id : selectedSource,
        target_account_id: filterOverrides.target_account_id !== undefined ? filterOverrides.target_account_id : selectedTarget,
        status: filterOverrides.status !== undefined ? filterOverrides.status : selectedStatus,
        beneficiary_added: filterOverrides.beneficiary_added !== undefined ? filterOverrides.beneficiary_added : selectedBeneficiary,
      };

      const params = {
        page,
        limit: pageSize,
      };

      if (currentFilters.search && currentFilters.search.trim()) {
        params.search = currentFilters.search.trim();
      }
      if (currentFilters.source_account_id && currentFilters.source_account_id !== 'all') {
        params.source_account_id = currentFilters.source_account_id;
      }
      if (currentFilters.target_account_id && currentFilters.target_account_id !== 'all') {
        params.target_account_id = currentFilters.target_account_id;
      }
      if (currentFilters.status && currentFilters.status !== 'all') {
        params.status = currentFilters.status;
      }
      if (currentFilters.beneficiary_added && currentFilters.beneficiary_added !== 'all') {
        params.beneficiary_added = currentFilters.beneficiary_added === 'true';
      }

      const response = await getFundRules(params);
      const data = response.data;
      
      const records = Array.isArray(data.data) ? data.data : 
                      Array.isArray(data.rules) ? data.rules :
                      Array.isArray(data) ? data : [];
      setRules(records);

      if (data.pagination) {
        setPagination({
          current: data.pagination.page,
          pageSize: data.pagination.limit,
          total: data.pagination.total,
        });
      } else if (data.total !== undefined) {
        setPagination(prev => ({ 
          ...prev, 
          total: data.total, 
          current: data.page || page,
          pageSize: data.limit || pageSize
        }));
      }
    } catch (err) {
      message.error('Failed to load fund rules.');
    } finally {
      setLoading(false);
    }
  }, [searchText, selectedSource, selectedTarget, selectedStatus, selectedBeneficiary]);

  const fetchBanks = useCallback(async () => {
    try {
      const response = await getBankList();
      const data = response.data;
      const records = Array.isArray(data.data) ? data.data : 
                      Array.isArray(data.banks) ? data.banks :
                      Array.isArray(data) ? data : [];
      setBanks(records);
    } catch (err) {
      message.error('Failed to load bank accounts.');
    }
  }, []);

  const fetchFundStatus = useCallback(async () => {
    setStatusLoading(true);
    try {
      const response = await getFundStatus();
      setFundSystemEnabled(response.data.enabled);
    } catch (err) {
      message.error('Failed to fetch Fund Automation System status.');
    } finally {
      setStatusLoading(false);
    }
  }, []);

  // Initial load
  useEffect(() => {
    fetchRules(1, 10);
    fetchBanks();
    fetchFundStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Debounced search effect
  const isSearchMounted = useRef(false);
  useEffect(() => {
    if (!isSearchMounted.current) {
      isSearchMounted.current = true;
      return;
    }
    const timer = setTimeout(() => {
      fetchRules(1, pagination.pageSize, { search: searchText });
    }, 350);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchText]);

  const handleSourceChange = (val) => {
    const value = val !== undefined && val !== null ? val : 'all';
    setSelectedSource(value);
    fetchRules(1, pagination.pageSize, { source_account_id: value });
  };

  const handleTargetChange = (val) => {
    const value = val !== undefined && val !== null ? val : 'all';
    setSelectedTarget(value);
    fetchRules(1, pagination.pageSize, { target_account_id: value });
  };

  const handleStatusChange = (val) => {
    const value = val || 'all';
    setSelectedStatus(value);
    fetchRules(1, pagination.pageSize, { status: value });
  };

  const handleBeneficiaryChange = (val) => {
    const value = val || 'all';
    setSelectedBeneficiary(value);
    fetchRules(1, pagination.pageSize, { beneficiary_added: value });
  };

  const handleResetFilters = () => {
    setSearchText('');
    setSelectedSource('all');
    setSelectedTarget('all');
    setSelectedStatus('all');
    setSelectedBeneficiary('all');
    fetchRules(1, pagination.pageSize, {
      search: '',
      source_account_id: 'all',
      target_account_id: 'all',
      status: 'all',
      beneficiary_added: 'all',
    });
  };

  const isFiltered = Boolean(
    (searchText && searchText.trim() !== '') ||
    selectedSource !== 'all' ||
    selectedTarget !== 'all' ||
    selectedStatus !== 'all' ||
    selectedBeneficiary !== 'all'
  );

  const handleTableChange = (newPagination) => {
    fetchRules(newPagination.current, newPagination.pageSize);
  };

  const handleStatusToggle = async (checked) => {
    setStatusLoading(true);
    try {
      await toggleFundStatus({ enabled: checked });
      setFundSystemEnabled(checked);
      message.success(`Fund Automation System ${checked ? 'enabled' : 'disabled'}.`);
    } catch (err) {
      message.error('Failed to update Fund Automation System status.');
      setFundSystemEnabled(!checked); // Revert on error
    } finally {
      setStatusLoading(false);
    }
  };

  const fetchAccountDependencies = useCallback(async () => {
    try {
      const [projRes, nameRes, countryRes, keyRes] = await Promise.all([
        getProjectList(),
        getBankNames(),
        getBankCountrys(),
        getFullKeyList()
      ]);
      setProjects(projRes.data);
      setBankNames(nameRes.data);
      setBankCountrys(countryRes.data);
      const formattedKeys = keyRes.data.map((key) => ({
        id: key.id,
        display: `${key.bank_name.toUpperCase()} | ${key.type.toUpperCase()}`,
      }));
      setKeys(formattedKeys);
    } catch (err) {
      message.error('Failed to load account creation dependencies.');
    }
  }, []);

  useEffect(() => {
    if (isAccountModalVisible) {
      fetchAccountDependencies();
    }
  }, [isAccountModalVisible, fetchAccountDependencies]);

  const handleOpenModal = (rule = null) => {
    setEditingRule(rule);
    const initialValues = rule 
      ? { ...rule, status: rule.is_active } 
      : { status: true, beneficiary_added: false };
    setBeneficiaryAdded(initialValues.beneficiary_added);
    form.setFieldsValue(initialValues);
    setIsModalVisible(true);
  };

  const handleCancel = () => {
    setIsModalVisible(false);
    setEditingRule(null);
    form.resetFields();
  };

  const handleOk = async () => {
    try {
      const values = await form.validateFields();
      const { status, ...rest } = values;
      const payload = { ...rest, is_active: status };
      if (editingRule) {
        await updateFundRule(editingRule.id, payload);
        message.success('Rule updated successfully.');
      } else {
        await createFundRule(payload);
        message.success('Rule created successfully.');
      }
      fetchRules(pagination.current, pagination.pageSize);
      handleCancel();
    } catch (err) {
      const errorDetail = err.response?.data?.detail || 'Failed to save rule.';
      message.error(errorDetail);
    }
  };

  const handleDelete = async (id) => {
    try {
      await deleteFundRule(id);
      message.success('Rule deleted successfully.');
      fetchRules(pagination.current, pagination.pageSize);
    } catch (err) {
      message.error('Failed to delete rule.');
    }
  };

  const handleCreateAccount = async () => {
    try {
      const values = await accountForm.validateFields();
      const payload = { ...values, answers, payout_system: 1 };
      await createBank(payload);
      message.success('Account successfully created.');
      setIsAccountModalVisible(false);
      accountForm.resetFields();
      setAnswers([{ key: '', value: '' }]);
      fetchBanks();
    } catch (err) {
      if (err.response?.data) {
        message.error(`Error creating account: ${JSON.stringify(err.response.data)}`);
      } else {
        message.error('Error creating account.');
      }
    }
  };

  const handleToggleRuleStatus = async (record, checked) => {
    try {
      await updateFundRule(record.id, { is_active: checked });
      message.success(`Rule ${checked ? 'enabled' : 'disabled'} successfully.`);
      fetchRules(pagination.current, pagination.pageSize);
    } catch (err) {
      message.error('Failed to update rule status.');
    }
  };

  const addAnswer = () => setAnswers([...answers, { key: '', value: '' }]);
  const removeAnswer = (index) => setAnswers(answers.filter((_, i) => i !== index));
  const updateAnswer = (index, field, value) => {
    const updatedAnswers = [...answers];
    updatedAnswers[index][field] = value;
    setAnswers(updatedAnswers);
  };

  const columns = [
    { 
      title: 'Rule ID', 
      dataIndex: 'id', 
      key: 'id', 
      width: 85,
      render: (id) => <Tag color="blue">#{id}</Tag>
    },
    { 
      title: 'Source Account', 
      dataIndex: 'source_account_name', 
      key: 'source_account_name',
      render: (text) => <strong>{text || 'N/A'}</strong>
    },
    { 
      title: 'Target Account', 
      dataIndex: 'target_account_name', 
      key: 'target_account_name',
      render: (text) => <strong>{text || 'N/A'}</strong>
    },
    { 
      title: 'Beneficiary Added', 
      dataIndex: 'beneficiary_added', 
      key: 'beneficiary_added', 
      render: (text) => (
        <Tag color={text ? 'success' : 'warning'}>
          {text ? 'Added' : 'Pending'}
        </Tag>
      ) 
    },
    { 
      title: 'Threshold', 
      dataIndex: 'threshold_amount', 
      key: 'threshold_amount', 
      render: (val) => (val !== undefined && val !== null ? `₹${Number(val).toLocaleString('en-IN')}` : '₹0')
    },
    { 
      title: 'Priority', 
      dataIndex: 'priority', 
      key: 'priority',
      render: (val) => <Tag color="purple">P{val}</Tag>
    },
    { 
      title: 'Status', 
      dataIndex: 'is_active', 
      key: 'is_active', 
      render: (active, record) => (
        <Space>
          <Switch 
            checked={active} 
            onChange={(checked) => handleToggleRuleStatus(record, checked)} 
          />
          <span style={{ fontSize: 12, color: active ? '#52c41a' : '#8c8c8c' }}>
            {active ? 'Active' : 'Inactive'}
          </span>
        </Space>
      ) 
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Button size="small" onClick={() => handleOpenModal(record)}>Edit</Button>
          <Button size="small" danger onClick={() => handleDelete(record.id)}>Delete</Button>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <div>
          <h2 style={{ margin: 0 }}>Fund Movement Rules</h2>
          <span style={{ fontSize: 13, color: '#8c8c8c' }}>
            Configure automated sweeping rules and thresholds between accounts
          </span>
        </div>
        <Space>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginRight: 8 }}>
            <span>Fund Automation System: </span>
            <Switch
              checked={fundSystemEnabled}
              onChange={handleStatusToggle}
              loading={statusLoading}
            />
          </div>
          <Button onClick={() => setIsAccountModalVisible(true)}>Add Account</Button>
          <Button type="primary" icon={<PlusOutlined />} onClick={() => handleOpenModal()}>Create Rule</Button>
        </Space>
      </div>

      {/* Search & Filter Toolbar */}
      <Card
        size="small"
        style={{
          marginBottom: 16,
          backgroundColor: '#fafafa',
          borderRadius: 8,
          border: '1px solid #f0f0f0',
        }}
        bodyStyle={{ padding: '12px 16px' }}
      >
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 12,
          }}
        >
          <Space wrap size="middle" style={{ flex: 1 }}>
            {/* Search Input */}
            <Input
              placeholder="Search rule ID, account name, login, number..."
              prefix={<SearchOutlined style={{ color: '#bfbfbf' }} />}
              allowClear
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              style={{ width: 280 }}
            />

            {/* Source Account Filter */}
            <Select
              placeholder="All Source Accounts"
              value={selectedSource}
              onChange={handleSourceChange}
              style={{ width: 210 }}
              showSearch
              optionFilterProp="children"
            >
              <Option value="all">All Source Accounts</Option>
              {banks.map((b) => (
                <Option key={`source-${b.id}`} value={b.id}>
                  {b.account_name || b.name || 'Account'} ({b.bank_login}) {b.payout_system ? '[Payout]' : '[Payin]'}
                </Option>
              ))}
            </Select>

            {/* Target Account Filter */}
            <Select
              placeholder="All Target Accounts"
              value={selectedTarget}
              onChange={handleTargetChange}
              style={{ width: 210 }}
              showSearch
              optionFilterProp="children"
            >
              <Option value="all">All Target Accounts</Option>
              {banks.map((b) => (
                <Option key={`target-${b.id}`} value={b.id}>
                  {b.account_name || b.name || 'Account'} ({b.bank_login}) {b.payout_system ? '[Payout]' : '[Payin]'}
                </Option>
              ))}
            </Select>

            {/* Status Filter */}
            <Select
              value={selectedStatus}
              onChange={handleStatusChange}
              style={{ width: 130 }}
            >
              <Option value="all">All Status</Option>
              <Option value="ACTIVE">Active</Option>
              <Option value="INACTIVE">Inactive</Option>
            </Select>

            {/* Beneficiary Added Filter */}
            <Select
              value={selectedBeneficiary}
              onChange={handleBeneficiaryChange}
              style={{ width: 170 }}
            >
              <Option value="all">All Beneficiaries</Option>
              <Option value="true">Beneficiary Added</Option>
              <Option value="false">Beneficiary Pending</Option>
            </Select>

            {/* Reset Filter Button */}
            <Button
              icon={<CloseCircleOutlined />}
              onClick={handleResetFilters}
              disabled={!isFiltered}
            >
              Reset
            </Button>
          </Space>

          {/* Result counter & Refresh */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontSize: 13, color: '#8c8c8c' }}>
              Showing {rules.length} of {pagination.total || 0} {pagination.total === 1 ? 'rule' : 'rules'}
            </span>
            {isFiltered && (
              <Tag color="blue" closable onClose={handleResetFilters}>
                Filtered
              </Tag>
            )}
            <Tooltip title="Refresh rules list">
              <Button
                type="text"
                icon={<ReloadOutlined />}
                onClick={() => fetchRules(pagination.current, pagination.pageSize)}
                loading={loading}
              />
            </Tooltip>
          </div>
        </div>
      </Card>

      <Table 
        dataSource={rules} 
        columns={columns} 
        loading={loading} 
        rowKey="id" 
        pagination={{
          ...pagination,
          showSizeChanger: true,
          pageSizeOptions: ['10', '20', '50'],
          showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} rules`,
        }}
        onChange={handleTableChange}
      />
      <Modal
        title={editingRule ? 'Edit Fund Rule' : 'Create Fund Rule'}
        visible={isModalVisible}
        onOk={handleOk}
        onCancel={handleCancel}
      >
        <Form form={form} layout="vertical">
          <Form.Item label="Source Account" required>
            <div style={{ display: 'flex', gap: '8px' }}>
              <Form.Item name="source_account_id" noStyle rules={[{ required: true, message: 'Please select a source account' }]}>
                <Select 
                  placeholder="Select source account" 
                  showSearch 
                  optionFilterProp="children"
                  getPopupContainer={trigger => trigger.parentNode}
                >
                  {banks.map(bank => (
                    <Option key={bank.id} value={bank.id}>{bank.account_name || 'No Name'} ({bank.bank_login}) {bank.role ? `(${bank.role})` : ''}</Option>
                  ))}
                </Select>
              </Form.Item>
              <Button icon={<PlusOutlined />} onClick={() => setIsAccountModalVisible(true)} />
            </div>
          </Form.Item>
          <Form.Item label="Target Account" required>
            <div style={{ display: 'flex', gap: '8px' }}>
              <Form.Item name="target_account_id" noStyle rules={[{ required: true, message: 'Please select a target account' }]}>
                <Select 
                  placeholder="Select target account" 
                  showSearch 
                  optionFilterProp="children"
                  getPopupContainer={trigger => trigger.parentNode}
                >
                  {banks.map(bank => (
                    <Option key={bank.id} value={bank.id}>{bank.account_name || 'No Name'} ({bank.bank_login}) {bank.role ? `(${bank.role})` : ''}</Option>
                  ))}
                </Select>
              </Form.Item>
              <Button icon={<PlusOutlined />} onClick={() => setIsAccountModalVisible(true)} />
            </div>
          </Form.Item>
          <Form.Item name="threshold_amount" label="Threshold Amount" rules={[{ required: true }]}>
            <InputNumber style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="beneficiary_added" valuePropName="checked" onChange={(e) => setBeneficiaryAdded(e.target.checked)}>
            <Checkbox>Beneficiary Added</Checkbox>
          </Form.Item>
          <Form.Item name="beneficiary_limit" label="Beneficiary Limit" rules={[{ required: true, message: 'Beneficiary Limit is required' }]}>
            <InputNumber style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item 
            name="priority" 
            label="Priority" 
            rules={[
              { required: true, message: 'Priority is required' },
              { type: 'number', min: 0, message: 'Priority must be at least 1' }
            ]}
          >
            <InputNumber style={{ width: '100%' }} precision={0} placeholder="Unique number per source account" />
          </Form.Item>
          <Form.Item
            noStyle
            shouldUpdate={(prevValues, currentValues) => prevValues.beneficiary_added !== currentValues.beneficiary_added}
          >
            {({ getFieldValue }) =>
              !getFieldValue('beneficiary_added') ? (
                <Form.Item name="beneficiary_cooldown" label="Beneficiary Cooldown (minutes)" rules={[{ required: true, message: 'Beneficiary Cooldown is required when beneficiary is not added' }]}>
                  <InputNumber style={{ width: '100%' }} min={0} />
                </Form.Item>
              ) : null
            }
          </Form.Item>
          <Form.Item name="cooldown_minutes" label="Cooldown (mins)" rules={[{ required: true }]}>
            <InputNumber style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="status" label="Status" valuePropName="checked">
            <Switch />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="Add Account"
        visible={isAccountModalVisible}
        onOk={handleCreateAccount}
        onCancel={() => setIsAccountModalVisible(false)}
      >
        <Form form={accountForm} layout="vertical">
          <Form.Item
            name="name"
            label="Bank Name"
            rules={[{ required: true, message: 'Select a bank name' }]}
          >
            <Select placeholder="Select a bank">
              {bankNames.map((name) => (
                <Option key={name} value={name}>
                  {name.toUpperCase()}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="country"
            label="Bank Country"
            rules={[{ required: true, message: 'Select a country' }]}
          >
            <Select placeholder="Select a country">
              {bankCountrys.map((name) => (
                <Option key={name} value={name}>
                  {name.toUpperCase()}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item name="role" label="Account Role">
            <Select placeholder="Select a role (optional)">
              <Option value="">None</Option>
              <Option value="Source">Source (For Fund Rules)</Option>
              <Option value="Pool">Pool (For Fund Rules)</Option>
              <Option value="CMS">CMS (For Fund Rules)</Option>
            </Select>
          </Form.Item>

          <Form.Item name="account_name" label="Account Name">
            <Input placeholder="Enter account name" />
          </Form.Item>
          <Form.Item name="bank_login" label="Login" rules={[{ required: true, message: 'Login is required' }]}>
            <Input placeholder="Enter login" />
          </Form.Item>
          <Form.Item name="bank_user_id" label="User ID">
            <Input placeholder="Enter user ID" />
          </Form.Item>
          <Form.Item name="bank_password" label="Password" rules={[{ required: true, message: 'Password is required' }]}>
            <Input.Password placeholder="Enter password" />
          </Form.Item>
          <Form.Item
            noStyle
            shouldUpdate={(prevValues, currentValues) => prevValues.name !== currentValues.name}
          >
            {({ getFieldValue }) =>
              getFieldValue('name') === 'indus'|| getFieldValue('name') === 'utkarsh' ? (
                <Form.Item
                  name="transaction_password"
                  label="Transaction Password"
                  rules={[{ required: true, message: 'Transaction Password is required for INDUS' }]}
                >
                  <Input.Password placeholder="Enter transaction password" />
                </Form.Item>
              ) : null
            }
          </Form.Item>
          <Form.Item
            noStyle
            shouldUpdate={(prevValues, currentValues) => prevValues.name !== currentValues.name}
          >
            {({ getFieldValue }) =>
              getFieldValue('name') === 'utkarsh' ? (
                <Form.Item
                  name="allowed_initiators"
                  label="Allowed Initiators"
                  rules={[{ required: true, message: 'Allowed Initiators is required for Utkarsh' }]}
                >
                  <Input placeholder="Enter allowed initiators" />
                </Form.Item>
              ) : null
            }
          </Form.Item>
          <Form.Item name="phone_number" label="Phone Number">
            <Input placeholder="Enter phone number" />
          </Form.Item>
          <Form.Item name="account_number" label="Account Number" rules={[{ required: true, message: 'Account Number is required' }]}>
            <Input placeholder="Enter account number" />
          </Form.Item>
          <Form.Item name="ifsc" label="IFSC Code" rules={[{ required: true, message: 'IFSC Code is required' }]}>
            <Input placeholder="Enter IFSC code" />
          </Form.Item>
          <Form.Item name="min_balance" label="Minimum Balance">
            <InputNumber style={{ width: '100%' }} placeholder="Enter minimum balance for rebalancing" />
          </Form.Item>
          <Form.Item name="corporate" valuePropName="checked">
            <Checkbox>Corporate Account</Checkbox>
          </Form.Item>
          </Form>
      </Modal>
    </div>
  );
};
export default FundRules;